import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Box, 
  Button, 
  Typography, 
  TextField, 
  FormControl, 
  FormControlLabel, 
  Checkbox, 
  CircularProgress,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  RadioGroup,
  Radio,
  FormLabel
} from '@mui/material';
import { Config } from '../../config/config';

// This is a placeholder component to satisfy build requirements
// The actual component is git-ignored and likely meant for local development only
const VideoUploadTest: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [waschstrasseDisplayName, setWaschstrasseDisplayName] = useState('');
  const [kameraName, setKameraName] = useState('');
  const [kameraRef, setKameraRef] = useState('');
  const [richtigAusgeloest, setRichtigAusgeloest] = useState<string>('false');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<any>(null);
  const [cameras, setCameras] = useState<any[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);

  // Fetch accounts with cameras on component mount
  useEffect(() => {
    fetchAccounts();
  }, []);

  // Handle changes in selected account
  useEffect(() => {
    // This effect will now primarily be for resetting camera selection
    // when the selected account changes. Camera data itself
    // will be managed by fetchAccounts and handleAccountChange.
    if (selectedAccount && selectedAccount.cameras) {
      setCameras(selectedAccount.cameras);
    } else {
      setCameras([]);
    }
    setKameraName('');
    setKameraRef('');
  }, [selectedAccount]);

  // Fetch accounts with their cameras
  const fetchAccounts = async () => {
    setLoadingAccounts(true);
    setError(null);
    setAccounts([]); // Clear previous accounts
    setCameras([]); // Clear previous cameras
    
    try {
      const apiUrl = Config.apiRoot;
      const token = localStorage.getItem('token');
      
      const accountsUrl = apiUrl.endsWith('/api') 
        ? `${apiUrl}/accounts?populateCameras=true` // Request population on the backend
        : `${apiUrl}/api/accounts?populateCameras=true`;
        
      const accountsResponse = await axios.get(
        accountsUrl, 
        {
          headers: {
            'Authorization': token ? `Bearer ${token}` : ''
          }
        }
      );

      console.log('Accounts response (with populated cameras):', accountsResponse.data);

      if (accountsResponse.data && Array.isArray(accountsResponse.data)) {
        const allAccounts = accountsResponse.data.map(account => {
          console.log('Processing account:', account.displayName, 'ID:', account._id, 'Cameras:', account.cameras);
          return {
            _id: account._id,
            displayName: account.displayName,
            // Ensure cameras are an array and have the necessary fields (_id, ref, displayName)
            cameras: (account.cameras || []).map((cam: any) => ({
              _id: cam._id, // Assuming cam object from backend has _id
              ref: cam.ref || cam._id, // Use cam.ref if available, otherwise fallback to _id
              displayName: cam.displayName || `Kamera ${cam.ref || cam._id}`
            }))
          };
        });
        
        console.log('Processed accounts for dropdown:', allAccounts);
        setAccounts(allAccounts);
        
        if (allAccounts.length > 0) {
          //setSelectedAccount(allAccounts[0]); // Let user select explicitly or handle default selection carefully
          //setWaschstrasseDisplayName(allAccounts[0].displayName);
          //setCameras(allAccounts[0].cameras || []); // Cameras will be set by handleAccountChange or if a default is selected
        } else {
          setError('Keine Accounts gefunden.');
        }
      } else {
        console.error('Unerwartetes Antwortformat für Accounts:', accountsResponse.data);
        setError('Die Accounts konnten nicht geladen werden. Unerwartetes Format der Antwort.');
      }
      
      setLoadingAccounts(false);
    } catch (err: any) {
      console.error('Error fetching accounts:', err);
      setError(err.response?.data?.message || err.message || 'Fehler beim Laden der Accounts.');
      setLoadingAccounts(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
    }
  };

  const handleAccountChange = async (e: any) => {
    const accountId = e.target.value;
    const account = accounts.find(acc => acc._id === accountId);
    
    setSelectedAccount(account);
    setWaschstrasseDisplayName(account?.displayName || '');
    
    // Reset camera selection first
    setKameraName('');
    setKameraRef('');
    setCameras([]); // Clear cameras immediately

    if (account) {
      // The account object from fetchAccounts should already contain its cameras
      // if the backend populates them correctly via ?populateCameras=true
      if (account.cameras && account.cameras.length > 0) {
        console.log(`Using pre-populated cameras for account ${account.displayName}:`, account.cameras);
        setCameras(account.cameras.map((cam: any) => ({ // Ensure format consistency
            _id: cam._id,
            ref: cam.ref || cam._id,
            displayName: cam.displayName || `Kamera ${cam.ref || cam._id}`
        })));
      } else {
        // Fallback: If cameras were not populated or account has no cameras, explicitly fetch or set empty
        console.log(`Account ${account.displayName} has no pre-populated cameras or camera array is empty. Attempting fetch or setting empty.`);
        // Optionally, you could re-fetch if necessary, but ideally, `populateCameras=true` handles this.
        // For now, we'll rely on the populated data. If it's empty, it means no cameras.
        setCameras([]); 
      }
    } else {
      setCameras([]); // No account selected, so no cameras
    }
  };

  const handleCameraChange = async (e: any) => {
    const selectedCameraRefValue = e.target.value; // This is camera.ref from renderCameraSelectItems
    const camera = cameras.find(cam => (cam.ref || cam._id) === selectedCameraRefValue);
    
    console.log("Selected camera ref value from dropdown:", selectedCameraRefValue);
    console.log("Found camera object:", camera);
    console.log("Current cameras in state for dropdown:", cameras);

    if (camera) {
      setKameraRef(camera._id); // Store the actual MongoDB ID as cameraRef for submission
      setKameraName(camera.displayName || `Kamera ${camera.ref || camera._id}`); // Store the display name for kameraName
      console.log(`Set kameraRef to ${camera._id} and kameraName to "${camera.displayName || `Kamera ${camera.ref || camera._id}`}"`);
    } else {
      setKameraRef('');
      setKameraName('');
      console.log("Camera not found for ref value:", selectedCameraRefValue, " Resetting kameraRef and kameraName.");
    }
  };

  const resetForm = () => {
    setFile(null);
    setRichtigAusgeloest('false');
    setResult(null);
    setError(null);
    // Keep the selected account and camera for convenience
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!file) {
      setError('Bitte wählen Sie eine Videodatei aus');
      return;
    }

    if (!selectedAccount) {
      setError('Bitte wählen Sie eine Waschstraße aus');
      return;
    }

    // Ensure both kameraRef (ID) and kameraName (display name) are set
    if (!kameraRef) { // kameraRef (the ID) is crucial
      setError('Bitte wählen Sie eine Kamera aus (ID nicht gesetzt)');
      return;
    }
    if (!kameraName) { // Check if kameraName (display name) is also set, though kameraRef is primary for logic
        setError('Bitte wählen Sie eine Kamera aus (Anzeigename nicht gesetzt)');
        return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('video', file);
      
      // Speichere die ObjectIDs statt der Namen
      formData.append('waschstrasse', selectedAccount._id); // ObjectID der Waschstraße
      formData.append('kameraName', kameraName); // ObjectID der Kamera
      formData.append('cameraRef', kameraRef);
      
      // Ensure we send the selected account ID
      formData.append('account', selectedAccount._id);
      
      // Add additional metadata
      formData.append('richtigAusgeloest', richtigAusgeloest);
      formData.append('timestamp', new Date().toISOString());

      // Get the API URL from Config instead of hardcoding
      const apiUrl = Config.apiRoot;
      
      // Get authentication token from local storage if available
      const token = localStorage.getItem('token');
      
      console.log('Uploading video with data:', {
        accountId: selectedAccount._id,
        accountName: selectedAccount.displayName,
        kameraId: kameraName,
        kameraRef: kameraRef,
        richtigAusgeloest,
        fileSize: file.size,
        fileName: file.name,
        apiUrl: apiUrl // Log the API URL for debugging
      });
      
      // Construct the proper URL - Config.apiRoot already includes '/api'
      const uploadUrl = apiUrl.endsWith('/api') 
        ? `${apiUrl}/videos/upload`
        : `${apiUrl}/api/videos/upload`;
        
      const response = await axios.post(
        uploadUrl, 
        formData, 
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': token ? `Bearer ${token}` : ''
          }
        }
      );

      setResult(response.data);
      setLoading(false);
      // Reset the form after successful upload, but keep account/camera selections
      setFile(null);
      setRichtigAusgeloest('false');
    } catch (err: any) {
      console.error('Error uploading video:', err);
      setError(err.response?.data?.message || err.message || 'Ein Fehler ist aufgetreten');
      setLoading(false);
    }
  };

  // Render camera select items based on the selected account
  const renderCameraSelectItems = () => {
    if (!selectedAccount) {
      return <MenuItem value="" disabled>Bitte zuerst Waschstraße wählen</MenuItem>;
    }
    
    // Cameras state should now be correctly populated for the selectedAccount
    if (cameras.length === 0) {
      return <MenuItem value="" disabled>Keine Kameras für diesen Account gefunden</MenuItem>;
    }
    
    return cameras.map(camera => (
      // Use camera.ref as value, as this is what handleCameraChange expects
      // Ensure camera.ref is unique and suitable as a key
      <MenuItem key={camera.ref || camera._id} value={camera.ref || camera._id}> 
        {camera.displayName || `Kamera ${camera.ref || camera._id}`}
      </MenuItem>
    ));
  };

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>Video Upload Test</Typography>
      <Typography variant="body1" paragraph>
        Mit diesem Formular können Sie testen, ob der Upload von Videos mit dem Google Service Account funktioniert.
      </Typography>

      <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box mb={2}>
              <Button
                variant="contained"
                component="label"
                fullWidth
                disabled={loading}
              >
                Videodatei auswählen
                <input
                  type="file"
                  accept="video/*"
                  hidden
                  onChange={handleFileChange}
                  value=""
                />
              </Button>
              {file && (
                <Typography variant="body2" mt={1}>
                  Ausgewählte Datei: {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                </Typography>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth margin="normal" disabled={loading || loadingAccounts}>
              <InputLabel id="waschstrasse-select-label">Waschstraße</InputLabel>
              <Select
                labelId="waschstrasse-select-label"
                value={selectedAccount?._id || ''}
                label="Waschstraße"
                onChange={handleAccountChange}
                disabled={loading || loadingAccounts || accounts.length === 0}
              >
                {loadingAccounts ? (
                  <MenuItem value="" disabled>Lädt...</MenuItem>
                ) : accounts.length === 0 ? (
                  <MenuItem value="" disabled>Keine Waschstraßen gefunden</MenuItem>
                ) : (
                  accounts.map(account => (
                    <MenuItem key={account._id} value={account._id}>
                      {account.displayName || `Account ${account._id}`}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth margin="normal" disabled={loading || !selectedAccount}>
              <InputLabel id="camera-select-label">Kamera</InputLabel>
              <Select
                labelId="camera-select-label"
                value={kameraRef}
                label="Kamera"
                onChange={handleCameraChange}
                disabled={loading || !selectedAccount || cameras.length === 0}
              >
                {renderCameraSelectItems()}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth margin="normal">
              <FormLabel id="richtig-ausgeloest-radio-group">Status der Auslösung</FormLabel>
              <RadioGroup
                aria-labelledby="richtig-ausgeloest-radio-group"
                name="richtigAusgeloest"
                value={richtigAusgeloest}
                onChange={(e) => setRichtigAusgeloest(e.target.value)}
                row
              >
                <FormControlLabel value="true" control={<Radio />} label="Richtig ausgelöst" disabled={loading} />
                <FormControlLabel value="false" control={<Radio />} label="Falsch ausgelöst" disabled={loading} />
                <FormControlLabel value="unknown" control={<Radio />} label="Unbekannt" disabled={loading} />
              </RadioGroup>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={loading || !file || !kameraRef}
              sx={{ mt: 3, mb: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Video hochladen'}
            </Button>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Button
              fullWidth
              variant="outlined"
              color="secondary"
              onClick={resetForm}
              disabled={loading}
              sx={{ mt: 3, mb: 2 }}
            >
              Formular zurücksetzen
            </Button>
          </Grid>
        </Grid>

        {error && (
          <Box mt={2} p={2} bgcolor="error.light" borderRadius={1}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}

        {result && (
          <Box mt={2} p={2} bgcolor="success.light" borderRadius={1}>
            <Typography variant="h6" gutterBottom>Upload erfolgreich!</Typography>
            <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
              {JSON.stringify(result, null, 2)}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default VideoUploadTest; 